import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Info, Send } from 'lucide-react';
import { useState } from 'react';
import type { DeskQuestion } from '../api/deskApi';
import { useAnswerDesk } from '../hooks/useDesk';

type Props = { q: DeskQuestion; canAnswer: boolean; role: string; onAnswered: () => void };

/** The bottom of an open question: a reply box for analysts, a note for anyone else. */
export function DeskReply({ q, canAnswer, role, onAnswered }: Props) {
  const [text, setText] = useState('');
  const send = useAnswerDesk();
  if (!canAnswer) {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
        <Info size={14} aria-hidden />
        <Typography variant="caption">Research analysts reply to desk questions. You&apos;re viewing this as {role === 'admin' ? 'an admin' : 'an RM'}.</Typography>
      </Stack>
    );
  }
  const submit = () => { send.mutate({ id: q.id, text: text.trim() }, { onSuccess: onAnswered }); };
  return (
    <Stack sx={{ gap: 1 }}>
      <TextField
        multiline
        minRows={3}
        maxRows={10}
        placeholder={`Reply to ${q.askedBy}. It goes back to them in ${q.channel === 'teams' ? 'Teams' : 'the portal'}.`}
        value={text}
        onChange={(e) => { setText(e.target.value); }}
        slotProps={{ htmlInput: { 'aria-label': 'Your answer' } }}
      />
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        {send.isError ? <Typography variant="caption" sx={{ color: 'error.main', mr: 'auto' }}>Could not send: {send.error.message}</Typography> : null}
        <Button variant="contained" size="small" endIcon={<Send size={14} />} disabled={send.isPending || text.trim().length < 2} onClick={submit} sx={{ ml: 'auto' }}>
          Send answer
        </Button>
      </Stack>
    </Stack>
  );
}
